<script setup lang="ts">
import { useAuthStore } from '@/stores/auth';
import { useTourStore } from '@/stores/components/tour';
import { navIcons } from '@/utils/nav';
import { getAvailableRoles } from '@/utils/organization';
import { getEnumNameFromRole } from '@/utils/role';
import { lowercaseFirstLetter } from '@/utils/text';
import { actionPointListAdminTourSteps, actionPointListTourSteps, manualTourSteps } from '@/utils/tour';

import { FeatureRole, OrganizationRole, type Role } from '@/enums/roles';

import { computedAsync } from '@vueuse/core';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';

const router = useRouter();

const { t: translate } = useI18n();

const emits = defineEmits(['open-menu']);

const authStore = useAuthStore();
const tourStore = useTourStore();

const { smAndDown } = useDisplay();

const currentOrgTypeName = computed(() => lowercaseFirstLetter(authStore.currentOrganization?.type ?? ''));

type ManualButton = { title: string; icon: string; routeName?: string; disabled: boolean; onClick?: () => void };

const exampleOrgTree = [
    {
        id: 1,
        title: 'Bedrijf A',
        children: [
            { id: 2, title: 'Vestiging B', children: [{ id: 3, title: 'Vestiging C' }] },
            { id: 4, title: 'Vestiging D' }
        ]
    }
];
const opened = ref<number[]>(getAllIds(exampleOrgTree));

const availableRoles = computedAsync(async () => await getAvailableRoles());

const buttons = computed(() => {
    const buttons: ManualButton[] = [];

    if (authStore.areMultipleOrganizationsAccessible) {
        buttons.push({ title: 'Bedrijf/vestiging wisselen', icon: 'business', disabled: smAndDown.value, onClick: () => emits('open-menu') });
    }

    buttons.push(
        { title: 'Notificaties', icon: navIcons.notifications, disabled: false, onClick: () => emits('open-menu') },
        { title: 'Profiel', icon: navIcons.profile, disabled: smAndDown.value, onClick: () => (!smAndDown.value ? emits('open-menu') : undefined) },
        {
            title: 'Dashboard',
            icon: navIcons.dashboard,
            disabled: smAndDown.value,
            onClick: () => (!smAndDown.value ? emits('open-menu') : undefined)
        },
        {
            title: 'Actielijst',
            icon: navIcons.actionPoints,
            routeName: 'actionPoints',
            disabled: smAndDown.value,
            onClick: () => (!smAndDown.value ? emits('open-menu') : undefined)
        },
        { title: 'Modules', icon: navIcons.manual, disabled: smAndDown.value, onClick: () => (!smAndDown.value ? emits('open-menu') : undefined) }
    );

    if (authStore.isAdmin)
        buttons.push({
            title: `Mijn ${currentOrgTypeName.value}`,
            icon: navIcons.myOrganization,
            disabled: smAndDown.value,
            onClick: () => emits('open-menu')
        });

    buttons.push({ title: 'Teams', icon: navIcons.teams, disabled: smAndDown.value, onClick: () => emits('open-menu') });

    return buttons;
});

function getAllIds(items: any[]): number[] {
    return items.flatMap((i) => [i.id, ...(i.children ? getAllIds(i.children) : [])]);
}

function getRoleExplanationText(role: Role) {
    switch (role) {
        case OrganizationRole.Admin:
            return 'kan zijn bedrijven beheren via het beheer en het mijn bedrijf menu. Heeft ook automatisch toegang tot alle onderliggende vestigingen van deze bedrijven.';
        case OrganizationRole.Employee:
            return 'kan alleen de actiepunten zien die ze zelf hebben aangemaakt, aan toegewezen zijn of die toegewezen zijn aan een team waar de medewerker in zit.';
        case FeatureRole.ActionCoordinator:
            return 'kan hetzelfde als een medewerker. Heeft ook automatisch toegang tot alle gegevens van onderliggende vestigingen waar deze medewerker is toegevoegd. Kan geen actiepunten verwijderen en niet de deadline en risicoklasse van een actiepunt aanpassen. Kan geen bedrijven/vestigingen beheren.';
        default:
            return '';
    }
}

function getRoleExplanationTitle(role: Role) {
    return translate(`enums.${getEnumNameFromRole(role)}.${role}`);
}

async function handleTourButtonClicked(index: number, routeName?: string) {
    if (routeName) {
        if (routeName === 'actionPoints') {
            const steps = [...actionPointListTourSteps];
            if (authStore.isAuthorized([OrganizationRole.Admin])) {
                steps.push(...actionPointListAdminTourSteps);
            }

            tourStore.steps = steps;
        }

        tourStore.continueTour = true;
        await router.push({ name: routeName });
        return;
    }

    const tourStep = manualTourSteps[index];
    if (!tourStep) return;

    tourStore.steps = [tourStep];
    tourStore.start();
}
</script>

<template>
    <v-fade-transition>
        <div>
            <p class="mb-0">
                Welkom bij de digitale handleiding voor 't Web Incontrol. In deze handleiding vindt u uitleg voor de meest gebruikte functies.

                <span v-if="smAndDown" class="d-block my-2">
                    Een aantal stappen binnen de digitale tour zijn nog niet beschikbaar in de mobiele variant. Gebruik hiervoor een tablet of
                    computer.
                </span>
            </p>
            <p class="mb-5">Klik op de knoppen hieronder voor een uitleg per functionaliteit.</p>

            <v-card max-width="300" class="m-0">
                <v-list>
                    <v-list-item
                        v-for="(button, index) of buttons"
                        :key="index"
                        :title="button.title"
                        :prepend-icon="button.icon"
                        class="hover-main"
                        :disabled="button.disabled"
                        @click="handleTourButtonClicked(index, button.routeName)"
                    />
                </v-list>
            </v-card>

            <v-expansion-panels class="mt-10" v-if="authStore.isAdmin">
                <v-expansion-panel title="Hoe werken bedrijven en vestigingen?">
                    <v-expansion-panel-text>
                        Een bedrijf kan meerdere onderliggende vestigingen hebben. Een vestiging kan ook weer meerdere vestigingen hebben. Op deze
                        manier is in principe elke organisatiestructuur in Incontrol te maken.<br />
                        Bedrijven worden aangemaakt en gekoppeld door 't Web. Vestigingen kunt u zelf aanmaken via mijn bedrijf ➔ vestigingen ➔
                        vestiging aanmaken. Een vestiging kan ook weer onderliggende vestigingen hebben.<br />
                        U kunt bijvoorbeeld de volgende structuur maken:
                        <v-treeview fluid density="compact" indent-lines :opened="opened" open-on-click item-value="id" :items="exampleOrgTree" />
                    </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel title="Wat betekenen de rollen voor een medewerker?">
                    <v-expansion-panel-text>
                        Bij het aanmaken van medewerkers kunnen rollen gekozen worden, dit is wat elke rol betekent:
                        <div class="mt-2" v-for="role of availableRoles" :key="role">
                            <strong>{{ getRoleExplanationTitle(role) }}</strong>
                            {{ getRoleExplanationText(role) }}
                        </div>
                        <div class="mt-3">
                            De optie "kan alleen lezen" kan ingesteld worden bij elke rol. Als dit is ingeschakeld kan de gebruiker alleen informatie
                            bekijken en dus geen gegevens aanmaken, wijzigen of verwijderen.
                        </div>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>
    </v-fade-transition>
</template>

<style scoped>
.v-list:deep() .v-icon {
    opacity: 0.9;
}
</style>
